import React from 'react';
import Head from 'next/head';
import Link from 'next/link';
import * as adsAjax from '../ajax/ads';
import { useRouter } from 'next/router';
import * as cartAjax from '../ajax/cart';
import Render from '../components/Render';
import type { Brand } from '../types/Brand';
import * as brandsAjax from '../ajax/brands';
import { useAjax } from '@developers/use-ajax';
import * as accountsAjax from '../ajax/accounts';
import styles from '../styles/HomePage.module.css';
import * as equipmentAjax from '../ajax/equipment';
import CartHistory from '../components/CartHistory';
import { useAuth } from '@developers/single-sign-on';
import Testimonials from '../components/Testimonials';
import useScreenResize from '../hooks/useScreenResize';
import { useScreenSize } from '../hooks/useScreenSize';
import * as testimonialsAjax from '../ajax/testimonials';
import DynamicAdCarousel from '../components/DynamicAdCarousel';
import GuidedEquipmentSearch from '../components/GuidedEquipmentSearch';
import AccountEquipmentList from '../components/AccountEquipmentList';
import OrderHistory from '../components/OrderHistory';
import SuggestedParts from '../components/SuggestedParts';
import CartFiller from '../components/CartFiller';
import FavoriteBrandsSearch from '../components/FavoriteBrandSearch';
import * as accountBrands from '../ajax/accountBrands';
import { Card, CardActionArea, CardContent, CardMedia } from '@mui/material';
import Typography from '@mui/material/Typography';
import DiscountLevel from '../components/DiscountLevel';
import Box from '@mui/material/Box';
import getProductIdsFromCartCookie from '../utils/getProductIdsFromCartCookie';

interface Props {
  brands: Brand[];
}

interface DiscountLevelDetails {
  parts_total_for_year: number,
  next_level: number,
  amount_to_next_level: number,
  discount_level: string
}

const initialFilterValues = {
  brand: '',
  subtype: ''
}

interface DiscountLevelDetails {
  parts_total_for_year: number,
  next_level: number,
  amount_to_next_level: number,
  discount_level: string
}

interface Testimonial {
  testimonial_text: string;
  testimonial_id: number;
}

// This function gets called at build time on server-side.
// It won't be called on client-side
export async function getStaticProps(context) {
  const brands = await (await brandsAjax.getBrands(true)).json();
  const ads = await (await adsAjax.getCarouselAds()).json();

  return {
    props: {
      brands,
      ads
    },
    revalidate: 86400, // 24hr
  }
}

interface Props {
  brands: Brand[];
}

function Home(props: Props) {

  const router = useRouter();
  const auth = useAuth();

  // Don't actually need these variables. Just need the page to rerender if the screen size changes after the initial render.
  const [width, height] = useScreenResize();

  const [accountType, setAccountType] = React.useState('');
  const [accountId, setAccountId] = React.useState(0);
  const [productIdsFromCart, setProductIdsFromCart] = React.useState([]);
  
  // First priority requests
  const [orders, getAccountOrders, getAccountOrdersMeta] = useAjax(accountsAjax.getAccountOrders);
  const [discountLevel, getDiscountLevelProgress, getDiscountLevelProgressMeta] = useAjax(accountsAjax.getDiscountLevelProgress, {} as DiscountLevelDetails);
  const [currentDiscountLevel, getDiscountLevel, getDiscountLevelMeta] = useAjax(accountsAjax.getDiscountLevel, {} as DiscountLevelDetails);
  const [cartHistory, getCartHistory, getCartHistoryMeta] = useAjax(accountsAjax.getCartHistory, []);
  const firstPriortyRequestsLoaded = getAccountOrdersMeta.success && getDiscountLevelProgressMeta.success && getDiscountLevelMeta.success && getCartHistoryMeta.success;

  // Second Priority Requests
  const [suggestedParts, getSuggestedParts, getSuggestedPartsMeta] = useAjax(accountsAjax.getSuggestedParts, []);
  const [accountEquipment, getAccountsEquipment, getAccountsEquipmentMeta] = useAjax(accountsAjax.getAccountsEquipment, {});
  const [cartFillerProducts, getCartFillerProducts, getCartFillerProductsMeta] = useAjax(cartAjax.getCartFillerProducts, []);
  const secondPriorityRequestsLoaded = getSuggestedPartsMeta.success && getAccountsEquipmentMeta.success && getCartFillerProductsMeta.success;

  const [testimonials, getRandomTestimonials, getRandomTestimonialMeta] = useAjax(testimonialsAjax.getRandomTestimonials, [] as Testimonial[]);
  const [updatedCart, addProductToCart, addProductToCartMeta] = useAjax(cartAjax.addProductToCart, {});
  const [ads, getCarouselAds, getCarouselAdsMeta] = useAjax(adsAjax.getCarouselAds, []);

  const [equipmentTypes, getEquipmentTypes, getEquipmentTypesMeta] = useAjax(equipmentAjax.getEquipmentTypesForBrand, []);
  const [equipmentSubtypes, getEquipmentSubtypesFromType, getEquipmentSubtypesFromTypeMeta] = useAjax(equipmentAjax.getEquipmentSubtypesFromType, []);
  const [equipment, getEquipmentByBrandAndSubtype, getEquipmentByBrandAndSubtypeMeta] = useAjax(equipmentAjax.getEquipmentByBrandAndSubtype, []);


  const [accountEquipmentBrands, getAccountsEquipmentBrands, getAccountsEquipmentBrandsMeta] = useAjax(accountsAjax.getAccountsEquipmentBrands, []);
  const [accountEquipmentSubTypes, getAccountsEquipmentSubTypes, getAccountsEquipmentSubTypesMeta] = useAjax(accountsAjax.getAccountsEquipmentSubTypes, []);

  const [favoriteBrands, getAccountBrands, getAccountBrandsMeta] = useAjax(accountBrands.getAccountBrands, []);
  const [addedBrand, updateAccountBrands, updateAccountBrandsMeta] = useAjax(accountBrands.updateAccountBrands, []);
  const [deletedBrand, deleteAccountBrand, deleteAccountBrandMeta] = useAjax(accountBrands.deleteAccountBrand, []);
  const [showErrorMessage, setShowErrorMessage] = React.useState(false);
  const [open, setOpen] = React.useState(false);


  const [error, setError] = React.useState(false);

  const [filterValues, setFilterValues] = React.useState(initialFilterValues);

  const [cookiePresent, setCookiePresent] = React.useState(null);

  React.useEffect(() => {
    const account_info_cookie = document.cookie.replace(/(?:(?:^|.*;\s*)account_info\s*\=\s*([^;]*).*$)|^.*$/, "$1");

    if (account_info_cookie) {
      setCookiePresent(true);
      const account_info_array = decodeURIComponent(account_info_cookie).split('|')

      const account_id = account_info_array[0];
      const account_type = account_info_array[1];

      setAccountType(account_type)
      setAccountId(parseInt(account_id));
      setProductIdsFromCart(getProductIdsFromCartCookie());
    } else {
      setCookiePresent(false);
      getCarouselAds();
      getRandomTestimonials(3);
    }
  }, []);

  // First priority requests if we have an account number
  React.useEffect(() => {
    if (accountId !== 0 && accountType !== 'Residential') {
      getAccountOrders(accountId);
      getDiscountLevelProgress(accountId);
      getDiscountLevel(accountId);
      getCartHistory(accountId);
    } else if (accountId !== 0 && accountType == 'Residential') {
      getAccountsEquipment(accountId);
      getAccountsEquipmentBrands(accountId);
      getCarouselAds();
      getRandomTestimonials(3);
    }
  }, [accountId, accountType]);

  // Second priority requests if we have an account number
  React.useEffect(() => {
    if (accountId !== 0 && accountType !== 'Residential' && firstPriortyRequestsLoaded) {
      getSuggestedParts(accountId, productIdsFromCart);
      getCartFillerProducts();
      getAccountsEquipment(accountId);
    }
  }, [accountId, accountType, firstPriortyRequestsLoaded]);

  // Third priority requests if we have an account number
  React.useEffect(() => {
    if (accountId !== 0 && accountType !== 'Residential' && secondPriorityRequestsLoaded) {
      getAccountsEquipmentBrands(accountId);
      getAccountBrands(accountId);
      getCarouselAds();
      getRandomTestimonials(3);
    }
  }, [accountId, accountType, secondPriorityRequestsLoaded]);

  // Account brands
  React.useEffect(() => {
    if (deleteAccountBrandMeta.success || updateAccountBrandsMeta.success) {
      getAccountBrands(accountId);
      handleClose();
    }
  }, [deleteAccountBrandMeta.success, updateAccountBrandsMeta.success]);

  // Account equipment
  React.useEffect(() => {
    if (accountId !== 0 && filterValues.brand !== '') {
      getAccountsEquipmentSubTypes(accountId, filterValues.brand);
    }
  }, [filterValues.brand]);

  React.useEffect(() => {
    if (updateAccountBrandsMeta.failure) {
      setShowErrorMessage(true);
    } else {
      setShowErrorMessage(false);
    }
  }, [updateAccountBrandsMeta.failure]);

  React.useEffect(() => {
    if (getAccountOrdersMeta.failure) {
      setError(true);
    } else {
      setError(false);
    }
  }, [getAccountOrdersMeta.failure]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    updateAccountBrandsMeta.reset();
  };

  function deleted(brand) {
    deleteAccountBrandMeta.reset()
    deleteAccountBrand(accountId, brand);
  }

  function submit(event, brand_id) {
    event.preventDefault();
    updateAccountBrands(accountId, brand_id);
    updateAccountBrandsMeta.reset();
  }

  function handleChange(e, field) {
    field == 'brand' ? setFilterValues({ ...filterValues, brand: e.target.value }) : setFilterValues({ ...filterValues, subtype: e.target.value });
  }

  function handleFilterClear() {   
    setFilterValues(initialFilterValues);
    getAccountsEquipment(accountId);
  }

  function handleSubmit() {
    getAccountsEquipment(accountId, filterValues);
  }

  function handlePagination(page, perPage) {
    getAccountsEquipment(accountId, { ...filterValues }, page,  perPage);
  }

  if (cookiePresent === null) {
    return null;
  }

  if (!cookiePresent || accountType == 'Residential') {
    return (
      <>
        <Head>
          <title>Fitness and Exercise Equipment Repair Parts</title>
          <meta
            name="description"
            content={'Find spare or replacement parts for exercise and fitness equipment. Use our diagrams and parts lists to locate the right part and extend the life of your treadmill, cross-trainer, bike, elliptical, stepper, or strength machine!'}
          />
          <meta httpEquiv="content-language" content="en-us" />
        </Head>
        <Welcome />
        <div className={styles.component}>
          <GuidedEquipmentSearch
            router={router}
            brands={props.brands}
            equipmentTypes={equipmentTypes}
            equipmentSubTypes={equipmentSubtypes}
            equipment={equipment}
            getEquipmentTypes={getEquipmentTypes}
            equipmentTypesMeta={getEquipmentTypesMeta}
            getEquipmentSubtypesFromType={getEquipmentSubtypesFromType}
            equipmentSubtypesMeta={getEquipmentSubtypesFromTypeMeta}
            getEquipmentByBrandAndSubtype={getEquipmentByBrandAndSubtype}
            equipmentMeta={getEquipmentByBrandAndSubtypeMeta}
          />
        </div>
        <div className={styles.component}>
          <ShopByPopularBrands />
        </div>
        <Render if={accountType == 'Residential'}>
          <div>
            <AccountEquipmentList
              filterValues={filterValues}
              handleChange={handleChange}
              handleFilterClear={handleFilterClear}
              handleSubmit={handleSubmit}
              accountEquipment={accountEquipment}
              accountEquipmentBrands={accountEquipmentBrands}
              accountEquipmentSubTypes={accountEquipmentSubTypes}
              handlePagination={handlePagination}
              loading={getAccountsEquipmentMeta.loading}
            />
          </div>
        </Render>
        <div className={styles.component}>
          <DynamicAdCarousel
            ads={ads}
            adsLoading={getCarouselAdsMeta.loading}
            width={width}
            router={router}
          />
        </div>
        <div style={{padding: '10px'}}>
          <Testimonials 
            testimonials={testimonials} 
            loading={getRandomTestimonialMeta.loading}
          />
        </div>
      </>
    );
  } else {
    return (
      <>
        <Head>
          <title>Fitness and Exercise Equipment Repair Parts</title>
          <meta
            name="description"
            content={'Find spare or replacement parts for exercise and fitness equipment. Use our diagrams and parts lists to locate the right part and extend the life of your treadmill, cross-trainer, bike, elliptical, stepper, or strength machine!'}
          />
          <meta httpEquiv="content-language" content="en-us" />
        </Head>
        <div className="container">
          <div className="row" style={{ paddingBottom: '20px' }}>
            <div className='col-sm-4'>
              <div className={styles.title} style={{marginLeft: '10px', marginBottom: '-10px'}}>
                Welcome Back
              </div>
              <div className={styles.button_container} style={{float: 'left', marginLeft: '15px', marginTop: '20px', marginBottom: '15px'}}>
                <button className={styles.title_button} onClick={() => {
                  if ((window as any).Tawk_API) {
                    (window as any).Tawk_API.maximize();
                  } else {
                    window.open('https://embed.tawk.to/6908cbee76c06c1953d12027/1j955p75d', '_blank');
                  }
                }}>Chat With Us</button>
              </div>
            </div>
            <div className='col-sm-8'>
              <div style={{ height: '250px', cursor: 'pointer' }}>
                <Link href='/inventory-system'>
                  <img src='/ad_images/smart-stock-banner.png' alt='Smart Stock Info Banner' style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </Link>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-4">
              <OrderHistory 
                orders={orders}
                error={error}
                loading={getAccountOrdersMeta.loading} 
              />
            </div>
            <div className={`col-sm-8 ${styles.cart_history}`}>
              <CartHistory
                data={cartHistory}
                meta={getCartHistoryMeta}
                screenWidth={width}
                discountLevel={discountLevel.discount_level}
                addProductToCart={addProductToCart}
                addProductToCartMeta={addProductToCartMeta}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-4">
              <DiscountLevel discountLevel={discountLevel} />
            </div>
            <div className={`col-sm-8 ${styles.guided_equipment_search}`}>
              <GuidedEquipmentSearch
                  router={router}
                  brands={props.brands}
                  equipmentTypes={equipmentTypes}
                  equipmentSubTypes={equipmentSubtypes}
                  equipment={equipment}
                  getEquipmentTypes={getEquipmentTypes}
                  equipmentTypesMeta={getEquipmentTypesMeta}
                  getEquipmentSubtypesFromType={getEquipmentSubtypesFromType}
                  equipmentSubtypesMeta={getEquipmentSubtypesFromTypeMeta}
                  getEquipmentByBrandAndSubtype={getEquipmentByBrandAndSubtype}
                  equipmentMeta={getEquipmentByBrandAndSubtypeMeta}
              />
            </div>
          </div>
        </div>
        <div style={{ marginTop: '50px', marginBottom: '50px' }}>
          {suggestedParts.length > 0 ? (
            <SuggestedParts
              data={suggestedParts}
              meta={getSuggestedPartsMeta}
              loading={(getSuggestedPartsMeta.loading || !firstPriortyRequestsLoaded) ? true : false}
              screenWidth={width}
              discountLevel={discountLevel.discount_level}
              addProductToCart={addProductToCart}
              addProductToCartMeta={addProductToCartMeta}
            />
          ) : (
            <CartFiller
              data={cartFillerProducts}
              screenWidth={width}
              meta={getCartFillerProductsMeta}
              loading={(getCartFillerProductsMeta.loading || !firstPriortyRequestsLoaded) ? true : false}
              addProductToCart={addProductToCart}
              addProductToCartMeta={addProductToCartMeta}
            />
          )}
        </div>
        <div style={{ marginBottom: '50px' }}>
          <AccountEquipmentList 
            screenWidth={width}
            filterValues={filterValues}
            handleChange={handleChange}
            handleFilterClear={handleFilterClear}
            handleSubmit={handleSubmit}
            accountEquipment={accountEquipment}
            accountEquipmentBrands={accountEquipmentBrands}
            accountEquipmentSubTypes={accountEquipmentSubTypes}
            handlePagination={handlePagination}
            loading={getAccountsEquipmentMeta.loading || !firstPriortyRequestsLoaded}
          />
        </div>
        <div style={{ marginBottom: '90px' }}>
          <FavoriteBrandsSearch 
            brands={props.brands}
            updateAccountBrands={updateAccountBrands}
            submit={submit}
            favoriteBrands={favoriteBrands.length == 0 ? props.brands : favoriteBrands}
            deleted={deleted}
            updateAccountBrandsMeta={updateAccountBrandsMeta}
            showErrorMessage={showErrorMessage}
            screenWidth={width}
            deleteMeta={deleteAccountBrandMeta}
            loggedIn={auth.isAuthenticated}
            handleClose={handleClose}
            handleClickOpen={handleClickOpen}
            setOpen={setOpen}
            open={open}
          />
        </div>
        <div style={{ marginBottom: '70px' }}>
          <DynamicAdCarousel 
              ads={ads}
              adsLoading={getCarouselAdsMeta.loading || !firstPriortyRequestsLoaded || !secondPriorityRequestsLoaded}
              width={width}
              router={router}
            />
        </div>
        <div style={{ padding: '10px' }}>
          <Testimonials 
            testimonials={testimonials} 
            loading={getRandomTestimonialMeta.loading || !firstPriortyRequestsLoaded || !secondPriorityRequestsLoaded}
          />
        </div>
      </>
    );
  }
};

export default Home;

function Welcome() {
  return (
    <div className={styles.welcome_message_container} itemScope itemType="http://schema.org/LocalBusiness">
      <div className={styles.content_wrapper}>
        <div className={styles.left_section}>
          <h2 className={styles.title}>Welcome To FitnessRepairParts.com</h2>
          <div className={styles.button_container}>
            <Link href='/company'><button className={styles.title_button}>About Us</button></Link>
            <Link href='/searchtips'><button className={styles.title_button}>How Do I Find Parts?</button></Link>
            <a href='/ticket.php?action=kb'><button className={styles.title_button}>FAQs</button></a>
            <button className={styles.title_button} onClick={() => (window as any).Tawk_API?.maximize()}>Live Chat</button>
          </div>
        </div>
        <div className={styles.right_section}>
          <p className={styles.paragraph}>
            Looking for Proform treadmill repair parts? How about a Life Fitness walking belt or that Nordic Track
            stop key? We maintain the largest online database of replacement parts for exercise and fitness equipment. Our unique
            and well organized cross reference search allows you to search by manufacturer part number or model number. Finding the
            right parts for your fitness equipment should be quick and easy.
          </p><br />
          <p className={styles.paragraph}>
            Please <a className={styles.link} href="/ticket.php?action=open">contact support via email</a> or through our Live Chat system for help. We 
            carry parts for sale for all the <Link href="/brand"><a className={styles.link}>major manufacturers</a></Link> such as: Bowflex, Schwinn, Monark, 
            Tunturi, Star Trac, Image, Health Rider, Proform, Weslo, Life Fitness, True Fitness, Precor, Cybex, and many more.
          </p>
        </div>
      </div>
    </div>
  );
}

function ShopByPopularBrands() {
  return (
    <>
      <div className={styles.section_title_container} style={{ marginTop: 15, textAlign: 'center' }}>
        <h2 className={styles.title}>Shop By Popular Brands</h2>
        <Link href='/brand'>
          <a className={styles.link} style={{ padding: '9px' }}>See All Brands &gt;</a>
        </Link>
      </div>
      <Box sx={{
        display: 'flex',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        overflowX: { xs: 'auto', md: 'visible' }, 
        justifyContent: { xs: 'center', md: 'space-between' },
        gap: 2,
        paddingBottom: 2,
        padding: '9px'
      }}>
        {[
          { id: 24, name: "SportsArt", img: "/brand_images/24.jpg" },
          { id: 43, name: "NordicTrack", img: "/brand_images/43.jpg" },
          { id: 52, name: "Parabody", img: "/brand_images/52.jpg" },
          { id: 66, name: "Bowflex", img: "/brand_images/66.jpg" },
          { id: 109, name: "Nautilus", img: "/brand_images/109.jpg" }
        ].map((brand) => (
          <Card key={brand.id} className={styles.card} sx={{
            height: 210,
            width: 150,
            boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#f9f9f9',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0px 8px 10px rgba(0,0,0,0.1)'
            }
          }}>
            <CardActionArea>
              <Link href={`/brand/${brand.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <a>
                  <CardMedia component="img"  sx={{
                    height: 135,
                    display: 'block', 
                    backgroundSize: 'cover', 
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center center',
                    width: '100%',
                    padding: '0em 1em 0px',
                    objectFit: 'contain'
                  }} 
                    image={brand.img} alt={brand.name} />
                  <CardContent>
                    <Typography variant="h5" component="div" sx={{ textAlign: 'center' }}>
                      {brand.name}
                    </Typography>
                  </CardContent>
                </a>
              </Link>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </>
  );
}